<template>
  <div>
    <v-autocomplete
      :value="value"
      :items="displayItems"
      item-text="label"
      item-value="id"
      :label="label"
      :multiple="multiple"
      :chips="chips"
      :clearable="clearable"
      :deletable-chips="deletableChips"
      :disabled="disabled"
      :error-messages="errorMessages"
      :rules="rules"
      :menu-props="{ closeOnClick: true, closeOnContentClick: false }"
      @change="handleChange"
      @input="handleInput"
    >
      <template v-slot:prepend-item>
        <template v-if="canAddNew">
          <v-list-item @mousedown.prevent @click="openAddCompanyModal">
            <v-list-item-icon>
              <v-icon>mdi-plus</v-icon>
            </v-list-item-icon>
            <v-list-item-content>
              <v-list-item-title>{{ $t("COMPANIES.ADD_NEW") }}</v-list-item-title>
            </v-list-item-content>
          </v-list-item>
          <v-divider class="mt-2"></v-divider>
        </template>
      </template>
    </v-autocomplete>

    <v-dialog v-model="showModal" max-width="900" persistent scrollable>
      <v-card :loading="isProcessing" :disabled="isProcessing">
        <template v-slot:progress>
          <v-progress-linear absolute color="success" height="4" indeterminate></v-progress-linear>
        </template>
        <v-card-title>{{ $t("COMPANIES.ADD_NEW") }}</v-card-title>
        <v-card-text>
          <v-form ref="requestData" v-model="validRequestData" @submit.prevent="storeCompany">
            <v-row>
              <v-col cols="12" md="6">
                <v-text-field
                  v-model="requestData.name"
                  :label="`${$t('GENERAL.LABELS.NAME')}*`"
                  required
                  :rules="[validations.required()]"
                  :error-messages="validationErrors.name || []"
                  @input="clearValidationError('name')"
                ></v-text-field>
              </v-col>
              <v-col cols="12" md="6">
                <v-autocomplete
                  v-model="requestData.company_rank_id"
                  :items="companyRanks"
                  item-text="label"
                  item-value="id"
                  :label="`${$t('COMPANIES.COMPANY_RANK')}*`"
                  required
                  :rules="[validations.required()]"
                  :error-messages="validationErrors.company_rank_id || []"
                  clearable
                  @input="clearValidationError('company_rank_id')"
                ></v-autocomplete>
              </v-col>
              <v-col cols="12" md="6">
                <v-autocomplete
                  v-model="requestData.company_type_ids"
                  :items="companyTypes"
                  item-text="label"
                  item-value="id"
                  :label="`${$t('COMPANIES.COMPANY_TYPES')}*`"
                  required
                  multiple
                  chips
                  small-chips
                  clearable
                  :rules="[(v) => (Array.isArray(v) && v.length > 0) || 'Item is required']"
                  :error-messages="validationErrors.company_type_ids || []"
                  @input="clearValidationError('company_type_ids')"
                ></v-autocomplete>
              </v-col>
              <v-col cols="12" md="6">
                <v-text-field
                  v-model="requestData.vat"
                  :label="$t('COMPANIES.VAT')"
                  :error-messages="validationErrors.vat || []"
                  @input="clearValidationError('vat')"
                ></v-text-field>
              </v-col>
              <v-col cols="12" md="6">
                <v-text-field
                  v-model="requestData.credit_limit"
                  :label="$t('COMPANIES.CREDIT_LIMIT')"
                  type="number"
                  step="0.01"
                  min="0"
                  :error-messages="validationErrors.credit_limit || []"
                  @input="clearValidationError('credit_limit')"
                ></v-text-field>
              </v-col>
              <v-col cols="12" md="6">
                <v-text-field
                  v-model="requestData.website"
                  :label="$t('COMPANIES.WEBSITE')"
                  :error-messages="validationErrors.website || []"
                  @input="clearValidationError('website')"
                ></v-text-field>
              </v-col>
              <v-col cols="12" md="6">
                <v-autocomplete
                  v-model="requestData.country_id"
                  :items="countries"
                  item-text="label"
                  item-value="id"
                  :label="$t('GENERAL.LABELS.COUNTRY')"
                  :error-messages="validationErrors.country_id || []"
                  clearable
                  @input="clearValidationError('country_id')"
                ></v-autocomplete>
              </v-col>
              <v-col cols="12" md="6">
                <v-autocomplete
                  v-model="requestData.owned_by"
                  :items="users"
                  item-text="name"
                  item-value="id"
                  :label="$t('COMPANIES.OWNED_BY')"
                  :error-messages="validationErrors.owned_by || []"
                  clearable
                  @input="clearValidationError('owned_by')"
                ></v-autocomplete>
              </v-col>
              <v-col cols="12">
                <v-row no-gutters align="start" class="address-field flex-nowrap">
                  <v-col>
                    <v-textarea
                      v-model="requestData.address"
                      :label="$t('COMPANIES.ADDRESS')"
                      rows="2"
                      auto-grow
                      :error-messages="validationErrors.address || []"
                      class="address-field__input"
                      @input="clearValidationError('address')"
                    ></v-textarea>
                  </v-col>
                  <v-col cols="auto" class="address-field__button-col">
                    <v-tooltip bottom>
                      <template v-slot:activator="{ on, attrs }">
                        <v-btn
                          class="address-field__button"
                          icon
                          color="primary"
                          v-bind="attrs"
                          v-on="on"
                          @click="showAddressBuilder = true"
                        >
                          <v-icon>mdi-plus</v-icon>
                        </v-btn>
                      </template>
                      <span>{{ $t("COMPANIES.ADDRESS_BUILDER") }}</span>
                    </v-tooltip>
                  </v-col>
                </v-row>
              </v-col>
            </v-row>

            <v-subheader class="px-0 general-emails-title">
              {{ $t("COMPANIES.GENERAL_EMAILS") }}
            </v-subheader>
            <v-row
              v-for="(generalEmail, index) in requestData.general_emails"
              :key="generalEmail._key || index"
              align="end"
            >
              <v-col cols="12" md="5">
                <v-text-field
                  :value="generalEmail.email"
                  :label="`${$t('GENERAL.LABELS.EMAIL')} *`"
                  :error-messages="generalEmailErrors(index, 'email')"
                  @input="updateGeneralEmail(index, 'email', $event)"
                />
              </v-col>
              <v-col cols="12" md="6">
                <FieldTagsInput
                  :value="generalEmail.tags"
                  field-tag-type-slug="company-email-types"
                  :label="$t('COMPANIES.EMAIL_TAGS')"
                  :error-messages="generalEmailTagErrors(index)"
                  @input="updateGeneralEmail(index, 'tags', $event)"
                />
              </v-col>
              <v-col cols="12" md="1" class="d-flex align-center">
                <v-btn
                  icon
                  color="error"
                  :aria-label="$t('GENERAL.LABELS.DELETE')"
                  @click="removeGeneralEmail(index)"
                >
                  <v-icon>mdi-delete</v-icon>
                </v-btn>
              </v-col>
            </v-row>

            <v-btn text color="primary" class="mt-0 mb-5" @click="addGeneralEmail">
              {{ $t("COMPANIES.ADD_GENERAL_EMAIL") }}
            </v-btn>

            <v-row>
              <v-col cols="12">
                <v-textarea
                  v-model="requestData.notes"
                  :label="$t('COMPANIES.NOTES')"
                  rows="3"
                  auto-grow
                  :error-messages="validationErrors.notes || []"
                  @input="clearValidationError('notes')"
                ></v-textarea>
              </v-col>
              <v-col cols="12">
                <v-checkbox
                  v-model="requestData.active"
                  class="mt-0"
                  :label="$t('GENERAL.LABELS.ACTIVE')"
                  hide-details
                  color="primary"
                />
              </v-col>
            </v-row>
          </v-form>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="warning" @click="closeAddCompanyModal">Cancel</v-btn>
          <v-btn color="success" @click="storeCompany">Create</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <AddressBuilderDialog
      v-model="showAddressBuilder"
      :parts="addressBuilderParts"
      :countries="countries"
      @apply="applyBuiltAddress"
    />
  </div>
</template>

<script>
import { mapGetters } from "vuex";
import {
  CREATE_COMPANY,
  GET_COMPANY_RANKS_OPTIONS,
  GET_COMPANY_TYPES_OPTIONS,
} from "@/store/companies.module";
import { GET_COUNTRIES } from "@/store/countries.module";
import { GET_USERS_OPTIONS } from "@/store/users.module";
import validations from "@/views/partials/form/Validations.vue";
import FieldTagsInput from "@/views/partials/form/FieldTagsInput.vue";
import AddressBuilderDialog, {
  cloneAddressParts,
} from "@/views/pages/companies/partials/AddressBuilderDialog.vue";
import { SHOW_SNACK } from "@/store/snack.module";

const getDefaultRequestData = (ownedBy = null) => ({
  name: "",
  company_rank_id: null,
  company_type_ids: [],
  active: true,
  vat: "",
  credit_limit: null,
  country_id: null,
  address: "",
  notes: "",
  owned_by: ownedBy,
  website: "",
  general_emails: [],
});

export default {
  name: "CompaniesAutocomplete",
  components: {
    FieldTagsInput,
    AddressBuilderDialog,
  },
  props: {
    value: {
      type: [Number, Array],
      default: null,
    },
    items: {
      type: Array,
      default: () => [],
    },
    label: {
      type: String,
      default: "",
    },
    multiple: {
      type: Boolean,
      default: false,
    },
    chips: {
      type: Boolean,
      default: false,
    },
    clearable: {
      type: Boolean,
      default: false,
    },
    deletableChips: {
      type: Boolean,
      default: false,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    errorMessages: {
      type: [String, Array],
      default: () => [],
    },
    rules: {
      type: Array,
      default: () => [],
    },
    showAddNew: {
      type: Boolean,
      default: true,
    },
  },
  data() {
    return {
      validations,
      showModal: false,
      showAddressBuilder: false,
      addressBuilderParts: cloneAddressParts(),
      isProcessing: false,
      validRequestData: false,
      validationErrors: {},
      generalEmailKeySeq: 0,
      companyRanks: [],
      companyTypes: [],
      countries: [],
      users: [],
      requestData: getDefaultRequestData(),
    };
  },
  computed: {
    ...mapGetters(["currentUser"]),
    displayItems() {
      return Array.isArray(this.items) ? this.items : [];
    },
    canAddNew() {
      return this.showAddNew && this.hasPermissions("companies-create");
    },
  },
  methods: {
    handleInput(value) {
      this.$emit("input", value);
    },
    handleChange(value) {
      this.$emit("change", value);
    },
    openAddCompanyModal() {
      if (!this.canAddNew) {
        return;
      }

      this.validationErrors = {};
      this.generalEmailKeySeq = 0;
      this.addressBuilderParts = cloneAddressParts();
      this.requestData = getDefaultRequestData(this.currentUser?.id || null);
      this.showModal = true;
      this.loadModalOptions();
    },
    closeAddCompanyModal() {
      this.showModal = false;
      this.showAddressBuilder = false;
    },
    getErrorMessage(error) {
      if (error && error.errors && typeof error.errors === "object") {
        const firstErrorField = Object.keys(error.errors)[0];
        const firstErrorList = error.errors[firstErrorField];

        if (Array.isArray(firstErrorList) && firstErrorList.length) {
          return firstErrorList[0];
        }
      }

      if (error && error.message) {
        return error.message;
      }

      return this.$t("GENERAL.UNEXPECTED_ERROR");
    },
    clearValidationError(field) {
      if (!this.validationErrors[field]) {
        return;
      }

      this.$delete(this.validationErrors, field);
    },
    applyBuiltAddress({ address, parts }) {
      this.requestData.address = address;
      this.addressBuilderParts = parts;
      this.clearValidationError("address");
    },
    addGeneralEmail() {
      this.requestData.general_emails = [
        ...this.requestData.general_emails,
        {
          _key: ++this.generalEmailKeySeq,
          id: null,
          email: "",
          tags: [],
        },
      ];
      this.clearGeneralEmailsValidationErrors();
    },
    removeGeneralEmail(index) {
      this.requestData.general_emails = this.requestData.general_emails.filter(
        (_, itemIndex) => itemIndex !== index
      );
      this.clearGeneralEmailsValidationErrors();
    },
    updateGeneralEmail(index, field, value) {
      this.requestData.general_emails = this.requestData.general_emails.map(
        (item, itemIndex) => {
          if (itemIndex !== index) {
            return item;
          }

          return {
            ...item,
            [field]: value,
          };
        }
      );
      this.clearGeneralEmailsValidationErrors();
    },
    generalEmailErrors(index, field) {
      const key = `general_emails.${index}.${field}`;
      const err = this.validationErrors[key];
      return Array.isArray(err) ? err : err ? [err] : [];
    },
    generalEmailTagErrors(index) {
      const prefix = `general_emails.${index}.tags`;
      return Object.keys(this.validationErrors)
        .filter((key) => key.startsWith(prefix))
        .flatMap((key) => {
          const err = this.validationErrors[key];
          return Array.isArray(err) ? err : err ? [err] : [];
        });
    },
    clearGeneralEmailsValidationErrors() {
      Object.keys(this.validationErrors).forEach((key) => {
        if (key.startsWith("general_emails.")) {
          this.$delete(this.validationErrors, key);
        }
      });
    },
    loadModalOptions() {
      return Promise.all([
        this.$store
          .dispatch(GET_COMPANY_RANKS_OPTIONS, {})
          .then((response) => {
            this.companyRanks = response.data || [];
          }),
        this.$store
          .dispatch(GET_COMPANY_TYPES_OPTIONS, {})
          .then((response) => {
            this.companyTypes = response.data || [];
          }),
        this.$store.dispatch(GET_COUNTRIES).then((response) => {
          this.countries = Array.isArray(response) ? response : response.data || [];
        }),
        this.$store.dispatch(GET_USERS_OPTIONS).then((response) => {
          this.users = response || [];
        }),
      ]).catch((error) => {
        this.$store.dispatch(SHOW_SNACK, {
          message: this.getErrorMessage(error),
          type: "error",
        });
      });
    },
    storeCompany() {
      this.$refs.requestData.validate();
      if (!this.validRequestData) {
        return;
      }

      this.validationErrors = {};
      this.isProcessing = true;
      this.$emit("processing");

      const payload = {
        ...this.requestData,
        general_emails: this.requestData.general_emails.map(({ id, email, tags }) => ({
          id,
          email,
          tags: (tags || []).map((tag) => ({
            id: tag.id || null,
            value: tag.value,
          })),
        })),
      };

      this.$store
        .dispatch(CREATE_COMPANY, payload)
        .then((company) => {
          const option = {
            id: Number(company.id),
            label: company.name,
          };

          this.$emit("company-created", option);
          this.$store.dispatch(SHOW_SNACK, {
            message: this.$t("COMPANIES.CREATED_MESSAGE", { name: company.name }),
            type: "success",
          });
          this.closeAddCompanyModal();
          this.$nextTick(() => {
            this.selectCreatedCompany(option.id);
          });
        })
        .catch((error) => {
          this.validationErrors = (error && error.errors) || {};
          this.$store.dispatch(SHOW_SNACK, {
            message: this.getErrorMessage(error),
            type: "error",
          });
        })
        .finally(() => {
          this.isProcessing = false;
          this.$emit("processed");
        });
    },
    selectCreatedCompany(companyId) {
      if (!companyId) {
        return;
      }

      if (this.multiple) {
        const current = Array.isArray(this.value) ? [...this.value] : [];
        if (!current.some((id) => Number(id) === Number(companyId))) {
          current.push(companyId);
        }
        this.handleInput(current);
        this.handleChange(current);
        return;
      }

      this.handleInput(companyId);
      this.handleChange(companyId);
    },
  },
};
</script>

<style scoped>
.address-field {
  flex-wrap: nowrap !important;
}

.address-field__input {
  margin-bottom: 0;
  min-width: 0;
}

.address-field__button-col {
  flex: 0 0 auto;
  padding-left: 8px;
}

.address-field__button {
  margin-top: 16px;
}

.general-emails-title {
  font-size: 1.3rem;
}
</style>
